// API URL Configuration — uses VITE_API_URL, else falls back to the current
// origin (so a misconfigured build hits its own host, not a hardcoded prod URL).
export const MAIN_URL = import.meta.env.VITE_API_URL
  || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
const API_BASE_URL = `${MAIN_URL}/api`;

const API_TIMEOUT_MS = 30000; // 30 seconds - increased for better reliability

// Get token from storage
const getToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('token');
  }
  return null;
};

// Fetch with timeout helper
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    // Handle abort errors specifically
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error details:', error);
        throw new Error('Network error. Unable to connect to the server. Please check if the backend is running.');
      }
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Retry wrapper with exponential backoff
async function fetchWithRetry(
  url: string, 
  options: RequestInit, 
  timeoutMs: number, 
  maxRetries: number = 2
): Promise<Response> {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      // Don't retry on authentication errors or 4xx status codes
      if (lastError.message.includes('Session expired') || 
          lastError.message.includes('Unauthorized')) {
        throw lastError;
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait with exponential backoff before retrying
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5 seconds
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

// API client
export const apiClient = {
  async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getToken();
    
    // Check if body is FormData - if so, don't set Content-Type (browser will set it)
    const isFormData = options.body instanceof FormData;
    
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    
    // Only set Accept and Content-Type for non-FormData requests
    if (!isFormData) {
      headers['Accept'] = 'application/json';
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetchWithRetry(url, {
        ...options,
        headers,
      }, API_TIMEOUT_MS);
      
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        localStorage.removeItem('token');
        // Only redirect to login if not already on login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please login again.');
      }
      
      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json().catch(() => null);
      } else {
        // If not JSON, get the text to help debug
        const text = await response.text();
        console.error('Non-JSON response from', url, ':', text.substring(0, 200));
        throw new Error(`Server returned ${response.status}: Not JSON. Check if backend is running.`);
      }
      
      if (!response.ok) {
        // Laravel returns errors in { message: '...' } or { errors: {...} } format
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        if (response.status === 422) {
          // Validation error - format the errors nicely
          if (data?.errors && typeof data.errors === 'object') {
            const errorList = Object.entries(data.errors)
              .map(([field, messages]: [string, any]) => {
                const msgs = Array.isArray(messages) ? messages.join(', ') : messages;
                return `${field}: ${msgs}`;
              })
              .join(' | ');
            errorMessage = `Validation Error: ${errorList}`;
          } else {
            errorMessage = data?.message || 'Validation failed. Please check your input.';
          }
        } else {
          errorMessage = data?.message || data?.error || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  },
  
  get(endpoint: string) {
    return this.request(endpoint, { method: 'GET' });
  },
  
  post(endpoint: string, data: unknown, options?: { headers?: Record<string, string> }) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...(options?.headers && { headers: options.headers }),
    });
  },
  
  put(endpoint: string, data: unknown) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  delete(endpoint: string) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  },
  
  postFormData(endpoint: string, formData: FormData) {
    const token = getToken();
    // For FormData, we must NOT set Content-Type - browser sets it automatically with boundary
    // Only send Authorization header if token exists
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Explicitly no Accept header either - let browser handle it
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers,
    });
  },
};

// Auth API
export const authApi = {
  login: (credentials: { email?: string; phone?: string; password: string }) =>
    apiClient.post('/login', credentials),
  
  logout: () => apiClient.post('/logout', {}),
  
  getUser: () => apiClient.get('/user'),
  
  impersonate: (userId: number) => apiClient.post(`/admin/impersonate/${userId}`, {}),
};

// Users (Staff) API
export const usersApi = {
  list: (params?: { role?: string; status?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/users${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/users/${id}`),
  create: (data: { name: string; email: string; password?: string; role: string; phone?: string; status?: string }) =>
    apiClient.post('/users', data),
  update: (id: number, data: { name?: string; email?: string; role?: string; phone?: string; status?: string }) =>
    apiClient.put(`/users/${id}`, data),
  delete: (id: number) => apiClient.delete(`/users/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getOverview: () => apiClient.get('/dashboard'),
  getStats: (period: string = 'month') => apiClient.get(`/dashboard/stats?period=${period}`),
};

// Settings API
export const settingsApi = {
  list: () => apiClient.get('/settings'),
  get: (key: string) => apiClient.get(`/settings/${key}`),
  getAll: () => apiClient.get('/settings'),
  
  /**
   * Get public settings (logo, business info) - no authentication required
   * Used for homepage header/footer
   */
  getPublic: async () => {
    try {
      const response = await fetch(`${MAIN_URL}/api/frontend-api/settings`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        // Don't throw error for public endpoint failures - just return empty data
        console.warn(`Public settings API returned status ${response.status}`);
        return { success: false, data: null };
      }
      
      return await response.json();
    } catch (error) {
      // Return empty data instead of throwing to prevent app crashes
      console.error('Public settings API call failed:', error);
      return { success: false, data: null };
    }
  },
  
  update: (settings: Record<string, any>) => {
    // Convert settings object to array format expected by backend
    const settingsArray = Object.entries(settings).map(([key, value]) => {
      // Map logoUrl and faviconUrl to theme.logoUrl and theme.faviconUrl
      let finalKey = key;
      if (key === 'logoUrl') finalKey = 'theme.logoUrl';
      if (key === 'faviconUrl') finalKey = 'theme.faviconUrl';
      
      return {
        key: finalKey,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        type: typeof value === 'object' ? 'json' : 'string',
      };
    });
    return apiClient.post('/settings', { settings: settingsArray });
  },
  updateByKey: (key: string, data: any) => {
    // For nested settings like 'business', 'general', etc.
    // Convert the nested object to individual settings
    if (typeof data === 'object' && data !== null) {
      const settingsArray = Object.entries(data).map(([subKey, value]) => ({
        key: `${key}.${subKey}`,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        type: typeof value === 'object' ? 'json' : 'string',
      }));
      return apiClient.post('/settings', { settings: settingsArray });
    }
    return apiClient.put(`/settings/${key}`, { value: data, type: 'string' });
  },
  updateSingle: (key: string, data: { value: any; type?: string; description?: string }) =>
    apiClient.put(`/settings/${key}`, data),
};

// Quotations API
export const quotationsApi = {
  list: (params?: string) => apiClient.get(`/quotations${params ? `?${params}` : ''}`),
  get: (id: number | string) => apiClient.get(`/quotations/${id}`),
  create: (data: unknown) => apiClient.post('/quotations', data),
  update: (id: number | string, data: unknown) => apiClient.put(`/quotations/${id}`, data),
  delete: (id: number | string) => apiClient.delete(`/quotations/${id}`),
  send: (id: number | string) => apiClient.post(`/quotations/${id}/send`, {}),
  updateStatus: (id: number | string, status: string) =>
    apiClient.post(`/quotations/${id}/update-status`, { status }),
  convertToEvent: (id: number | string, payload?: any) => apiClient.post(`/quotations/${id}/convert-to-event`, payload || {}),
  clone: (id: number | string) => apiClient.post(`/quotations/${id}/clone`, {}),
  getLogs: (id: number | string) => apiClient.get(`/quotations/${id}/logs`),
};

// Clients API
export const clientsApi = {
  list: (params?: string) => apiClient.get(`/clients${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/clients/${id}`),
  create: (data: unknown) => apiClient.post('/clients', data),
  update: (id: number, data: unknown) => apiClient.put(`/clients/${id}`, data),
  delete: (id: number) => apiClient.delete(`/clients/${id}`),
  getEvents: (id: number) => apiClient.get(`/clients/${id}/events`),
  getPayments: (id: number) => apiClient.get(`/clients/${id}/payments`),
  getLedger: (id: number, params?: string) => apiClient.get(`/clients/${id}/ledger${params ? `?${params}` : ''}`),
};

// Room Bookings
export const bookingsApi = {
  list: (params?: { event_id?: number; from_date?: string; to_date?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/room-bookings${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/room-bookings/${id}`),
  create: (data: unknown) => apiClient.post('/room-bookings', data),
  update: (id: number, data: unknown) => apiClient.put(`/room-bookings/${id}`, data),
  delete: (id: number) => apiClient.delete(`/room-bookings/${id}`),
};

export const roomBookingsApi = bookingsApi;

// Events API
export const eventsApi = {
  list: (params?: string) => apiClient.get(`/events${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/events/${id}`),
  create: (data: unknown) => apiClient.post('/events', data),
  createPastEvent: (data: unknown) => apiClient.post('/events/past', data),
  update: (id: number, data: unknown) => apiClient.put(`/events/${id}`, data),
  delete: (id: number) => apiClient.delete(`/events/${id}`),
  updateStatus: (id: number, status: string) =>
    apiClient.post(`/events/${id}/update-status`, { status }),
  addPayment: (id: number, data: unknown) =>
    apiClient.post(`/events/${id}/add-payment`, data),
  getFinancials: (id: number) => apiClient.get(`/events/${id}/financials`),
  getInvoices: (id: number) => apiClient.get(`/events/${id}/invoices`),
  getTasks: (id: number) => apiClient.get(`/events/${id}/tasks`),
  getCalendar: (params?: string) => apiClient.get(`/events/calendar${params || ''}`),
  getVendorPaymentSummary: (id: number) => apiClient.get(`/events/${id}/vendor-payments/summary`),
  getVendorPayments: (id: number) => apiClient.get(`/events/${id}/vendor-payments`),
  addVendorPayment: (id: number, data: unknown) => apiClient.post(`/events/${id}/vendor-payments`, data),
  deleteVendorPayment: (eventId: number, paymentId: number) => apiClient.delete(`/events/${eventId}/vendor-payments/${paymentId}`),
  getLogs: (id: number) => apiClient.get(`/events/${id}/logs`),
  // Event Vendors (assign vendors to events)
  addEventVendor: (eventId: number, data: unknown) => apiClient.post(`/events/${eventId}/vendors`, data),
  removeEventVendor: (eventId: number, vendorId: number) => apiClient.delete(`/events/${eventId}/vendors/${vendorId}`),
  // Event Services (add services to events)
  addEventService: (eventId: number, data: unknown) => apiClient.post(`/events/${eventId}/services`, data),
  removeEventService: (eventId: number, serviceId: number) => apiClient.delete(`/events/${eventId}/services/${serviceId}`),
  // Room Bookings for events
  getRoomBookings: (eventId: number) => apiClient.get(`/room-bookings?event_id=${eventId}`),
  addRoomBooking: (eventId: number, data: unknown) => apiClient.post('/room-bookings', { ...data as object, event_id: eventId }),
  removeRoomBooking: (bookingId: number) => apiClient.delete(`/room-bookings/${bookingId}`),
  updateRoomBooking: (bookingId: number, data: unknown) => apiClient.put(`/room-bookings/${bookingId}`, data),
};

// Vendors API
export const vendorsApi = {
  list: (params?: string) => apiClient.get(`/vendors${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/vendors/${id}`),
  create: (data: unknown) => apiClient.post('/vendors', data),
  update: (id: number, data: unknown) => apiClient.put(`/vendors/${id}`, data),
  delete: (id: number) => apiClient.delete(`/vendors/${id}`),
  getEvents: (id: number) => apiClient.get(`/vendors/${id}/events`),
  getPayments: (id: number) => apiClient.get(`/vendors/${id}/payments`),
  getLedger: (id: number) => apiClient.get(`/vendors/${id}/ledger`),
  getAllPayments: (params?: { vendor_id?: number; per_page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/vendors/payments/all${queryParams ? `?${queryParams}` : ''}`);
  },
};

// Tasks API
export const tasksApi = {
  list: (params?: string) => apiClient.get(`/tasks${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/tasks/${id}`),
  create: (data: unknown) => apiClient.post('/tasks', data),
  update: (id: number, data: unknown) => apiClient.put(`/tasks/${id}`, data),
  delete: (id: number) => apiClient.delete(`/tasks/${id}`),
  complete: (id: number) => apiClient.post(`/tasks/${id}/complete`, {}),
  getMyTasks: () => apiClient.get('/tasks/my-tasks'),
};

// Invoices API
export const invoicesApi = {
  list: (params?: { status?: string; from_date?: string; to_date?: string; search?: string; per_page?: number; page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/invoices${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/invoices/${id}`),
  create: (data: any) => apiClient.post('/invoices', data),
  generateFromEvent: (eventId: number, data?: { notes?: string }) => 
    apiClient.post(`/invoices/generate/${eventId}`, data || {}),
  update: (id: number, data: any) => apiClient.put(`/invoices/${id}`, data),
  delete: (id: number) => apiClient.delete(`/invoices/${id}`),
  download: async (id: number) => {
    // Fetch with the auth token (window.open would omit it and 401), then save the blob.
    const token = getToken();
    const url = `${API_BASE_URL}/invoices/${id}/download`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error('Failed to download invoice');
    }
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `invoice-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  },
  markAsPaid: (id: number) => apiClient.post(`/invoices/${id}/mark-paid`, {}),
  recalculate: (id: number) => apiClient.post(`/invoices/${id}/recalculate`, {}),
};

// Invoice Settings API
export const invoiceSettingsApi = {
  get: () => apiClient.get('/invoice-settings'),
  update: (data: any) => apiClient.put('/invoice-settings', data),
  resetAllNumbers: () => apiClient.post('/invoices/reset-all-numbers', {}),
};

// Quotation Settings API
export const quotationSettingsApi = {
  get: () => apiClient.get('/quotation-settings'),
  update: (data: any) => apiClient.put('/quotation-settings', data),
  resetAllNumbers: () => apiClient.post('/quotations/reset-all-numbers', {}),
};

// Expenses API
export const expensesApi = {
  list: (params?: string) => apiClient.get(`/expenses${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/expenses/${id}`),
  create: (data: unknown) => apiClient.post('/expenses', data),
  update: (id: number, data: unknown) => apiClient.put(`/expenses/${id}`, data),
  delete: (id: number) => apiClient.delete(`/expenses/${id}`),
  getCategories: () => apiClient.get('/expenses/categories/list'),
};

// Enquiries API
export const enquiriesApi = {
  list: (params?: string) => apiClient.get(`/enquiries${params ? `?${params}` : ''}`),
  get: (id: number) => apiClient.get(`/enquiries/${id}`),
  create: (data: unknown) => apiClient.post('/enquiries', data),
  update: (id: number, data: unknown) => apiClient.put(`/enquiries/${id}`, data),
  delete: (id: number) => apiClient.delete(`/enquiries/${id}`),
  convert: (id: number, data: unknown) =>
    apiClient.post(`/enquiries/${id}/convert`, data),
  convertToQuotation: (id: number, data?: unknown) =>
    apiClient.post(`/enquiries/${id}/convert-to-quotation`, data || {}),
};

// Masters API
export const mastersApi = {
  getAll: () => apiClient.get('/masters/all'),
  
  // Expense Categories
  expenseCategories: {
    list: () => apiClient.get('/masters/expense-categories'),
    create: (data: unknown) => apiClient.post('/masters/expense-categories', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/expense-categories/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/expense-categories/${id}`),
  },
  
  // Vendor Types
  vendorTypes: {
    list: () => apiClient.get('/masters/vendor-types'),
    create: (data: unknown) => apiClient.post('/masters/vendor-types', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/vendor-types/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/vendor-types/${id}`),
  },
  
  // Services
  services: {
    list: () => apiClient.get('/masters/services'),
    create: (data: unknown) => apiClient.post('/masters/services', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/services/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/services/${id}`),
  },
  
  // Packages
  packages: {
    list: () => apiClient.get('/masters/packages'),
    create: (data: unknown) => apiClient.post('/masters/packages', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/packages/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/packages/${id}`),
  },
  
  // Rooms
  rooms: {
    list: () => apiClient.get('/masters/rooms'),
    create: (data: unknown) => apiClient.post('/masters/rooms', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/rooms/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/rooms/${id}`),
  },

  // Vendors
  vendors: {
    list: () => apiClient.get('/masters/vendors'),
    create: (data: unknown) => apiClient.post('/masters/vendors', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/vendors/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/vendors/${id}`),
  },
  
  // Venues
  venues: {
    list: () => apiClient.get('/venues'),
    create: (data: unknown) => apiClient.post('/venues', data),
    update: (id: number, data: unknown) => apiClient.put(`/venues/${id}`, data),
    delete: (id: number) => apiClient.delete(`/venues/${id}`),
    getAvailability: (id: number, fromDate: string, toDate: string) =>
      apiClient.get(`/venues/${id}/availability?from_date=${fromDate}&to_date=${toDate}`),
    checkAllAvailability: (startDate: string, endDate: string) =>
      apiClient.get(`/availability/venues?start_date=${startDate}&end_date=${endDate}`),
  },

  // Vendor Payments
  vendorPayments: {
    list: (params?: { 
      from_date?: string; 
      to_date?: string; 
      vendor_id?: number;
      vendor_type_id?: number;
      payment_method?: string;
      event_id?: number;
      per_page?: number;
      page?: number;
    }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/reports/vendor-payments${queryParams ? `?${queryParams}` : ''}`);
    },
    summary: (params?: { from_date?: string; to_date?: string; vendor_id?: number }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/reports/vendor-payments/summary${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/vendors/payments', data),
    delete: (id: number) => apiClient.delete(`/vendor-payments/${id}`),
  },

  // Event Types
  eventTypes: {
    list: (params?: { status?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/masters/event-types${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/masters/event-types', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/event-types/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/event-types/${id}`),
  },

  // Payment Methods
  paymentMethods: {
    list: (params?: { status?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/masters/payment-methods${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/masters/payment-methods', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/payment-methods/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/payment-methods/${id}`),
  },

  // Testimonials
  testimonials: {
    list: (params?: { is_active?: boolean; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/testimonials/all${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/testimonials', data),
    update: (id: number, data: unknown) => apiClient.put(`/testimonials/${id}`, data),
    delete: (id: number) => apiClient.delete(`/testimonials/${id}`),
  },

  // Room Bookings
  roomBookings: {
    list: (params?: { event_id?: number; from_date?: string; to_date?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/room-bookings${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/room-bookings', data),
    update: (id: number, data: unknown) => apiClient.put(`/room-bookings/${id}`, data),
    delete: (id: number) => apiClient.delete(`/room-bookings/${id}`),
  },

  // Activity Logs
  activityLogs: {
    list: (params?: { user_id?: number; action?: string; from_date?: string; to_date?: string; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/activity-logs${queryParams ? `?${queryParams}` : ''}`);
    },
    stats: (params?: { from_date?: string; to_date?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/activity-logs/stats${queryParams ? `?${queryParams}` : ''}`);
    },
    actions: () => apiClient.get('/activity-logs/actions'),
  },

  // Payment Logs
  paymentLogs: {
    list: (params?: { payment_type?: string; action?: string; client_id?: number; vendor_id?: number; event_id?: number; from_date?: string; to_date?: string; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/payment-logs${queryParams ? `?${queryParams}` : ''}`);
    },
    stats: (params?: { from_date?: string; to_date?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/payment-logs/stats${queryParams ? `?${queryParams}` : ''}`);
    },
    actions: () => apiClient.get('/payment-logs/actions'),
  },
};

// Testimonials API (standalone export)
export const testimonialsApi = {
  list: (params?: { is_active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/testimonials/all${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/testimonials', data),
  update: (id: number, data: unknown) => apiClient.put(`/testimonials/${id}`, data),
  delete: (id: number) => apiClient.delete(`/testimonials/${id}`),
};

// Client Payments API
export const clientPaymentsApi = {
  list: (params?: { client_id?: number; event_id?: number; from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/client-payments${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/client-payments/${id}`),
  create: (data: any) => apiClient.post('/client-payments', data),
  update: (id: number, data: any) => apiClient.put(`/client-payments/${id}`, data),
  delete: (id: number) => apiClient.delete(`/client-payments/${id}`),
};

// Reports API
export const reportsApi = {
  financialSummary: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/financial-summary${queryParams ? `?${queryParams}` : ''}`);
  },
  vendorPayments: (params?: { from_date?: string; to_date?: string; vendor_id?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/vendor-payments${queryParams ? `?${queryParams}` : ''}`);
  },
  clientPayments: (params?: { from_date?: string; to_date?: string; client_id?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/client-payments${queryParams ? `?${queryParams}` : ''}`);
  },
  expenseBreakdown: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/expense-breakdown${queryParams ? `?${queryParams}` : ''}`);
  },
  // Vendor Reports
  vendorPerformance: (params?: { from_date?: string; to_date?: string; vendor_id?: number; vendor_type_id?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/vendor-performance${queryParams ? `?${queryParams}` : ''}`);
  },
  vendorAssignments: (params?: { from_date?: string; to_date?: string; vendor_id?: number; status?: string; per_page?: number; page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/vendor-assignments${queryParams ? `?${queryParams}` : ''}`);
  },
  vendorComparison: (params?: { vendor_ids?: number[]; vendor_type_id?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.vendor_ids) {
      params.vendor_ids.forEach(id => queryParams.append('vendor_ids[]', String(id)));
    }
    if (params?.vendor_type_id) {
      queryParams.append('vendor_type_id', String(params.vendor_type_id));
    }
    const queryString = queryParams.toString();
    return apiClient.get(`/reports/vendor-comparison${queryString ? `?${queryString}` : ''}`);
  },
  vendorCommissions: (params?: { from_date?: string; to_date?: string; vendor_id?: number; commission_type?: string; per_page?: number; page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/vendor-commissions${queryParams ? `?${queryParams}` : ''}`);
  },
  eventDetailReport: (eventId: number) => {
    return apiClient.get(`/reports/event-detail/${eventId}`);
  },
  revenueTrends: (params?: { year?: number; from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/revenue-trends${queryParams ? `?${queryParams}` : ''}`);
  },
  clientAnalytics: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/client-analytics${queryParams ? `?${queryParams}` : ''}`);
  },
  enquiryFunnel: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/enquiry-funnel${queryParams ? `?${queryParams}` : ''}`);
  },
  packageAnalytics: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/package-analytics${queryParams ? `?${queryParams}` : ''}`);
  },
  venueUtilization: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/venue-utilization${queryParams ? `?${queryParams}` : ''}`);
  },
  roomRevenue: (params?: { from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/reports/room-revenue${queryParams ? `?${queryParams}` : ''}`);
  },
};

// Event Types API
export const eventTypesApi = {
  list: (params?: { status?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/masters/event-types${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/masters/event-types', data),
  update: (id: number, data: unknown) => apiClient.put(`/masters/event-types/${id}`, data),
  delete: (id: number) => apiClient.delete(`/masters/event-types/${id}`),
};

// Payment Methods API
export const paymentMethodsApi = {
  list: (params?: { status?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/masters/payment-methods${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/masters/payment-methods', data),
  update: (id: number, data: unknown) => apiClient.put(`/masters/payment-methods/${id}`, data),
  delete: (id: number) => apiClient.delete(`/masters/payment-methods/${id}`),
};

// Image Upload API
export const imageApi = {
  upload: async (file: File, type: 'logo' | 'favicon' | 'gallery' | 'pwa', settingKey?: string) => {
    const formData = new FormData();
    // Ensure the file has a proper name with extension
    const fileName = file.name || `${type}_${Date.now()}.jpg`;
    formData.append('image', file, fileName);
    formData.append('type', type);
    if (settingKey) {
      formData.append('setting_key', settingKey);
    }

    const token = getToken();
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/upload/image`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
        API_TIMEOUT_MS
      );

      if (response.status === 401) {
        localStorage.removeItem('token');
        // Only redirect to login if not already on login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please login again.');
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || 'Upload failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Upload failed');
    }
  },

  delete: (path: string, settingKey?: string) => {
    const params = new URLSearchParams();
    params.append('path', path);
    if (settingKey) {
      params.append('setting_key', settingKey);
    }
    return apiClient.delete(`/upload/image?${params.toString()}`);
  },
};

// FAQs API
export const faqsApi = {
  list: (params?: { category?: string; is_active?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/faqs${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/faqs/${id}`),
  create: (data: unknown) => apiClient.post('/faqs', data),
  update: (id: number, data: unknown) => apiClient.put(`/faqs/${id}`, data),
  delete: (id: number) => apiClient.delete(`/faqs/${id}`),
  getCategories: () => apiClient.get('/faqs/categories/list'),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/faqs/update-order', { items }),
  toggleActive: (id: number) => apiClient.post(`/faqs/${id}/toggle-active`, {}),
};

// Features API
export const featuresApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/features${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/features/${id}`),
  create: (data: unknown) => apiClient.post('/features', data),
  update: (id: number, data: unknown) => apiClient.put(`/features/${id}`, data),
  delete: (id: number) => apiClient.delete(`/features/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/features/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/features/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/features/${id}/toggle-active`, {}),
};

// Galleries API
export const galleriesApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/galleries${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/galleries/${id}`),
  create: (data: unknown) => apiClient.post('/galleries', data),
  update: (id: number, data: unknown) => apiClient.put(`/galleries/${id}`, data),
  delete: (id: number) => apiClient.delete(`/galleries/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/galleries/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/galleries/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/galleries/${id}/toggle-active`, {}),
};

// Instagram Reels API
export const instagramReelsApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/instagram-reels${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/instagram-reels/${id}`),
  create: (data: unknown) => apiClient.post('/instagram-reels', data),
  update: (id: number, data: unknown) => apiClient.put(`/instagram-reels/${id}`, data),
  delete: (id: number) => apiClient.delete(`/instagram-reels/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/instagram-reels/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/instagram-reels/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/instagram-reels/${id}/toggle-active`, {}),
};

// Profile API
export const profileApi = {
  get: () => apiClient.get('/profile'),
  update: (data: { name?: string; email?: string }) => apiClient.put('/profile', data),
  changePassword: (data: { current_password: string; password: string; password_confirmation: string }) =>
    apiClient.post('/profile/change-password', data),
};

// Health check for backend availability
export const healthApi = {
  check: async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      }, 5000); // 5 second timeout for health check
      
      return response.ok;
    } catch (error) {
      return false;
    }
  },
};

// Data Management API
export const dataManagementApi = {
  /**
   * Get summary of all data counts
   */
  getSummary: () => apiClient.get('/data/summary'),
  
  /**
   * Get detailed database statistics
   */
  getStats: () => apiClient.get('/data/stats'),
  
  /**
   * Reset data (requires confirmation)
   * @param preserveUsers - Whether to preserve user accounts (default: true)
   * @param type - Type of reset: 'all' or 'operational' (events/clients/enquiries only)
   */
  resetData: (preserveUsers: boolean = true, type: 'all' | 'operational' = 'all') =>
    apiClient.post('/data/reset', { confirm: true, preserve_users: preserveUsers, type }),
  
  /**
   * Generate dummy data for testing
   * @param count - Number of records to generate (optional)
   */
  generateDummyData: (count?: number) =>
    apiClient.post('/data/generate-dummy', count ? { count } : {}),
  
  /**
   * Export all data to JSON (chunked for large datasets)
   * @param table - Optional: export specific table only
   * @param chunkSize - Number of records per chunk (default: 1000)
   */
  exportData: (table?: string, chunkSize?: number) => {
    const params = new URLSearchParams();
    if (table) params.append('table', table);
    if (chunkSize) params.append('chunk_size', chunkSize.toString());
    const query = params.toString();
    return apiClient.get(`/data/export${query ? `?${query}` : ''}`);
  },
  
  /**
   * Import data from JSON (batched for large datasets)
   * @param data - The data to import
   * @param mergeStrategy - How to handle conflicts: 'replace', 'merge', or 'skip'
   * @param batchSize - Number of records per batch (default: 500)
   */
  importData: (data: any, mergeStrategy: 'replace' | 'merge' | 'skip' = 'skip', batchSize?: number) =>
    apiClient.post('/data/import', { data, merge_strategy: mergeStrategy, batch_size: batchSize }),
};

// Site Settings API (Frontend Website Settings)
export const siteSettingsApi = {
  /**
   * Get site settings
   */
  get: () => apiClient.get('/site-settings'),
  
  /**
   * Update site settings
   */
  update: (data: {
    hero_title?: string;
    hero_subtitle?: string;
    hero_images?: string[];
    stats_capacity?: string;
    stats_events?: string;
    stats_experience?: string;
    stats_satisfaction?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_address?: string;
    social_facebook?: string;
    social_instagram?: string;
    social_twitter?: string;
    footer_about?: string;
  }) => apiClient.put('/site-settings', data),
  
  /**
   * Upload hero image
   */
  uploadHeroImage: (formData: FormData) => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return apiClient.request('/site-settings/hero-image', {
      method: 'POST',
      body: formData,
      headers,
    });
  },
  
  /**
   * Delete hero image
   */
  deleteHeroImage: (url: string) => apiClient.delete(`/site-settings/hero-image?url=${encodeURIComponent(url)}`),
  
  /**
   * Get public site settings (no auth required)
   */
  getPublic: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-settings/public`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch public site settings:', error);
      throw error;
    }
  },
};

export const rolePageAccessApi = {
  me: () => apiClient.get(`/role-access/me`),
  index: () => apiClient.get(`/role-access`),
  update: (role: string, access: Record<string, boolean>) =>
    apiClient.put(`/role-access`, { role, access }),
};
